import { z } from 'zod';

import { ChartSchema, ChartSeriesSchema } from '#core/components/dashboard/panels/chart/schema.ts';
import { KpiSchema } from '#core/components/dashboard/panels/kpi/schema.ts';
import { TableSchema } from '#core/components/dashboard/panels/table/schema.ts';
import { GridItemSchema, GridLayoutSchema } from '#core/components/dashboard/schema.ts';
import { PositionType } from '#core/schema/defs.ts';

import { inputDatasetSchema, sourceRefSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';

/**
 * Dashboard output schema.
 */
export const ShowDashboardOutputSchema = z.object({
  success: z.literal(true),
  message: z.string(),
});

/** @public */
export type ShowDashboardOutput = z.infer<typeof ShowDashboardOutputSchema>;

/**
 * LLM-facing series schema: each series declares its own dataset via
 * `inputDataset` + `column`. This replaces the item-level `inputDataset` for
 * chart items, enabling overlays of series that live in different
 * datasets (e.g. daily + weekly-averaged of the same metric). The chart
 * adapter (AdaptedChart) resolves and merges these into core's classic
 * `ChartSeries` shape, so core chart code is unchanged.
 *
 * Reuses {@link sourceRefSchema}'s `{ inputDataset, column }` shape — both
 * fields enforce `min(1)` and inherit the shared base description.
 * Overlay semantics ("different series MAY reference different datasets")
 * are documented in the chart-series guidance inside `ShowDashboardTool`'s
 * `ToolGuidance` block, not here, to keep schema descriptions tool-agnostic.
 */
export const LlmChartSeriesSchema = ChartSeriesSchema.extend(sourceRefSchema.shape);
/**
 * Inferred input type for {@link LlmChartSeriesSchema}. Use this in
 * consumers (e.g. AdaptedChart) so the type stays in sync with the schema.
 *
 * @public
 */
export type LlmChartSeries = z.input<typeof LlmChartSeriesSchema>;

/**
 * LLM-facing chart schema: same as core's `ChartSchema` but with
 * `LlmChartSeriesSchema` in place of `ChartSeriesSchema`. Chart items
 * have no item-level `inputDataset` — each series carries its own `inputDataset`.
 * `data` is inherited from `ChartSchema` and supports inline data as a
 * fallback when the LLM constructs a chart without referencing a dataset.
 */
const LlmChartSchema = ChartSchema.omit({ series: true }).extend({
  series: z
    .array(LlmChartSeriesSchema)
    .optional()
    .describe(
      'Multi-series configuration. Each entry names a `inputDataset` + `column`; entries MAY reference different datasets and will be overlaid on a shared `xAxis`.',
    ),
});

/**
 * LLM-facing KPI schema: same as core's `KpiSchema` but with `value`
 * widened to accept either a number or a pre-formatted display string.
 */
const LlmKpiSchema = KpiSchema.extend({
  value: z.union([z.number(), z.string()]).describe('A concrete pre-computed value (number or display string).'),
});

const LlmTableSchema = TableSchema.extend({
  inputDataset: inputDatasetSchema.optional().describe('Reference to a stored dataset by its outputDataset.'),
});

/**
 * LLM-facing grid item schema. Composes core's `GridItemSchema` (which
 * carries the shared item-level fields `caption` / `captionPosition` /
 * `layer`) with three plugin-specific overrides:
 * - `position` — upgraded from optional to required (the LLM should always specify).
 * - `inputDataset` — added as an optional reference to a staged dataset
 *   (applies to `table` and `kpi` items only; chart items use per-series refs).
 * - `component` — swapped to the LLM-facing union so `chart` carries
 *   `LlmChartSeriesSchema` and `kpi` accepts a string `value`.
 *
 * Presentation fields (`caption`, `captionPosition`, `layer`) stay at item
 * level — there is no LLM/core hoist; the LLM input shape is structurally
 * identical to core's `GridItemSchema` apart from the `inputDataset`
 * extension and the LLM-facing component variants.
 */
export const LlmGridItemSchema = GridItemSchema.extend({
  position: PositionType.describe('Grid position and span'),
  component: z
    .union([
      LlmChartSchema,
      LlmTableSchema,
      LlmKpiSchema,
      // MarkdownSchema
    ])
    .describe('Component configuration'),
});

export const ShowDashboardInputSchema = z.object({
  layout: GridLayoutSchema,
  items: LlmGridItemSchema.array().describe('Grid items to render'),
  title: z.string().optional().describe('Optional dashboard title'),
});

/** @public */
export type ShowDashboardInput = z.infer<typeof ShowDashboardInputSchema>;
